import React from 'react';
import { createGlobalStyle } from 'styled-components';
import { LayoutGroup, motion } from 'framer-motion';
import { theme } from 'antd';
import { DragOutlined, PlusOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay, PointerSensor, useDroppable, useDraggable, useSensor, useSensors } from '@dnd-kit/core';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { BlockerCard } from '@/components/module/projects/blockers_tab/BlockerCard';
import { BLOCKER_KANBAN_DRAG_CLASS } from '@/components/module/projects/blockers_tab/BlockerDragWrap';
import { getBlockerApiId } from '@hooks/projects/projectHelpers';
import { BLOCKER_KANBAN_COL } from '@hooks/projects/useProjectBlockers';
import { useSprintKanbanSyncHorizontalScroll } from '@hooks/projects/useProjectSprints';
import { useMatchMedia } from '@hooks/useMatchMedia';
import { SprintKanbanScrollChainStyle } from '@/components/module/projects/sprints_tab/SprintKanbanBoard';
import {
    SprintKanbanFill,
    KanbanThreeColumnRow,
    KanbanColumnPanel,
    KanbanColumnHeader,
    KanbanColumnTitle,
    KanbanColumnCountBadge,
    TaskCardKanbanStatusPillCount,
    KanbanColumnBody,
    KanbanDragDropPlaceholder,
    BoardCollapseSectionHeading,
    kanbanDesktopFixedHeaderCellStyle,
    kanbanDesktopFixedBoardRowInnerStyle,
    kanbanDesktopFixedBoardHeaderRowInnerStyle,
} from '@/pages/inside/tenant/style';

const COL = { ACTIVE: 'todo', RESOLVED: 'completed' };

const KANBAN_LAYOUT_DURATION = 0.52;
const KANBAN_LAYOUT_EASE = [0.22, 1, 0.36, 1];

const BlockerKanbanDragCursorStyle = createGlobalStyle`
    body.${BLOCKER_KANBAN_DRAG_CLASS} {
        cursor: grabbing !important;
        user-select: none !important;
    }
    .blocker-kanban-task-drag img,
    .blocker-kanban-task-drag a {
        -webkit-user-drag: none;
    }
    .blocker-kanban-task-drag [data-kanban-drag-handle] {
        cursor: grab;
        -webkit-user-drag: element;
        touch-action: none;
    }
    .blocker-kanban-task-drag [data-kanban-drag-handle]:active {
        cursor: grabbing;
    }
    /* Drag handle — same as sprint Kanban: show on card hover */
    .blockers-kanban-root .blocker-kanban-task-drag [data-kanban-task-card-actions] {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.18s ease;
    }
    .blockers-kanban-root .blocker-kanban-task-drag:hover [data-kanban-task-card-actions] {
        opacity: 1;
        pointer-events: auto;
    }
    .blockers-kanban-root .blocker-kanban-task-drag [data-kanban-task-card-actions]:focus-within {
        opacity: 1;
        pointer-events: auto;
    }
    .blockers-kanban-root .blocker-kanban-task-drag [data-kanban-drag-handle]:focus-visible {
        outline: 2px solid var(--ant-color-primary, #1677ff);
        outline-offset: 2px;
    }
    @media (hover: none) {
        .blockers-kanban-root .blocker-kanban-task-drag [data-kanban-task-card-actions] {
            opacity: 0.85;
            pointer-events: auto;
        }
    }
`;

const COLUMN_DROP_MESSAGES = {
    [BLOCKER_KANBAN_COL.ACTIVE]: 'Drop here to move to Active',
    [BLOCKER_KANBAN_COL.RESOLVED]: 'Drop here to mark resolved',
};

/**
 * Blockers tab: two-column Kanban — Active | Resolved (drag between columns when allowed).
 */
export default function BlockersKanbanBoard({
    activeBlockers,
    resolvedBlockers,
    onAddBlocker,
    onBlockerSelect,
    canDragBlockers,
    dropTarget,
    draggingColumn,
    onDndDragStart,
    onDndDragOverColumn,
    onDndDragEnd,
}) {
    const { token } = theme.useToken();
    const isNarrow = useMatchMedia('(max-width: 1024px)');
    const { headerScrollRef, bodyScrollRef, onHeaderScroll, onBodyScroll } = useSprintKanbanSyncHorizontalScroll();
    const total = (Array.isArray(activeBlockers) ? activeBlockers.length : 0) + (Array.isArray(resolvedBlockers) ? resolvedBlockers.length : 0);

    const showDragPlaceholder = (columnId) => Boolean(canDragBlockers && draggingColumn && draggingColumn !== columnId);
    const dropZoneActive = (columnId) =>
        Boolean(canDragBlockers && draggingColumn && draggingColumn !== columnId && dropTarget === columnId);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
    );

    const [activeDragPayload, setActiveDragPayload] = React.useState(null);

    const handleDragStart = React.useCallback(
        (event) => {
            const payload = event?.active?.data?.current ?? null;
            setActiveDragPayload(payload);
            onDndDragStart?.(payload);
            document.body.classList.add(BLOCKER_KANBAN_DRAG_CLASS);
        },
        [onDndDragStart],
    );

    const handleDragOver = React.useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            if (overColumnId) onDndDragOverColumn?.(String(overColumnId), payload);
        },
        [onDndDragOverColumn],
    );

    const handleDragEnd = React.useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            onDndDragEnd?.({ overColumnId: overColumnId != null ? String(overColumnId) : null, payload });
            setActiveDragPayload(null);
            document.body.classList.remove(BLOCKER_KANBAN_DRAG_CLASS);
        },
        [onDndDragEnd],
    );

    const handleDragCancel = React.useCallback(() => {
        setActiveDragPayload(null);
        document.body.classList.remove(BLOCKER_KANBAN_DRAG_CLASS);
    }, []);

    function DroppableKanbanColumnPanel({ columnId, children, ...panelProps }) {
        const { setNodeRef } = useDroppable({ id: columnId });
        return (
            <KanbanColumnPanel ref={setNodeRef} {...panelProps}>
                {children}
            </KanbanColumnPanel>
        );
    }

    function DraggableBlockerCard({ blocker, sourceColumn, children }) {
        const blockerId = getBlockerApiId(blocker) ?? blocker?.id;
        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
            id: `blocker-${String(blockerId)}`,
            data: { blockerId, sourceColumn },
            disabled: !canDragBlockers,
        });
        const style = transform
            ? {
                transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            }
            : undefined;
        return (
            <div
                ref={setNodeRef}
                className="blocker-kanban-task-drag"
                style={{ ...style, opacity: isDragging ? 0.6 : 1, width: '100%', minWidth: 0 }}
                {...attributes}
                {...listeners}
            >
                {children}
            </div>
        );
    }

    const renderCard = (b, column) => {
        const bid = getBlockerApiId(b);
        const layoutId = `blocker-kanban-${String(bid ?? 'x')}`;
        const layoutCard = (
            <motion.div
                layout
                layoutId={layoutId}
                style={{ minHeight: 0 }}
                transition={{
                    layout: {
                        duration: KANBAN_LAYOUT_DURATION,
                        ease: KANBAN_LAYOUT_EASE,
                    },
                }}
            >
                <BlockerCard blocker={b} column={column} onSelect={onBlockerSelect} />
            </motion.div>
        );
        if (canDragBlockers && bid) {
            return (
                <DraggableBlockerCard
                    key={bid}
                    blocker={b}
                    sourceColumn={column === 'active' ? BLOCKER_KANBAN_COL.ACTIVE : BLOCKER_KANBAN_COL.RESOLVED}
                >
                    {layoutCard}
                </DraggableBlockerCard>
            );
        }
        return (
            <React.Fragment key={bid ?? layoutId}>{layoutCard}</React.Fragment>
        );
    };

    const activeColumnBody = (
        <KanbanColumnBody
            className="sprint-kanban-column-body-natural"
            $qaStack
            data-drag-placeholder={showDragPlaceholder(BLOCKER_KANBAN_COL.ACTIVE) ? 'true' : undefined}
            $dragPlaceholder={showDragPlaceholder(BLOCKER_KANBAN_COL.ACTIVE)}
        >
            {showDragPlaceholder(BLOCKER_KANBAN_COL.ACTIVE) ? (
                <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(BLOCKER_KANBAN_COL.ACTIVE)}>
                    <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                    <span>{COLUMN_DROP_MESSAGES[BLOCKER_KANBAN_COL.ACTIVE]}</span>
                </KanbanDragDropPlaceholder>
            ) : (
                activeBlockers.map((b) => renderCard(b, 'active'))
            )}
        </KanbanColumnBody>
    );

    const resolvedColumnBody = (
        <KanbanColumnBody
            className="sprint-kanban-column-body-natural"
            $qaStack
            data-drag-placeholder={showDragPlaceholder(BLOCKER_KANBAN_COL.RESOLVED) ? 'true' : undefined}
            $dragPlaceholder={showDragPlaceholder(BLOCKER_KANBAN_COL.RESOLVED)}
        >
            {showDragPlaceholder(BLOCKER_KANBAN_COL.RESOLVED) ? (
                <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(BLOCKER_KANBAN_COL.RESOLVED)}>
                    <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                    <span>{COLUMN_DROP_MESSAGES[BLOCKER_KANBAN_COL.RESOLVED]}</span>
                </KanbanDragDropPlaceholder>
            ) : (
                resolvedBlockers.map((b) => renderCard(b, 'resolved'))
            )}
        </KanbanColumnBody>
    );

    const kanbanRow = (
        <SprintKanbanFill className="sprint-kanban-fill-natural">
            <LayoutGroup id="blockers-kanban-board">
                {isNarrow ? (
                    <KanbanThreeColumnRow className="sprint-kanban-column-row-natural" style={{ marginTop: 0 }}>
                        <DroppableKanbanColumnPanel columnId={BLOCKER_KANBAN_COL.ACTIVE} $fixedDesktop>
                            <KanbanColumnHeader>
                                <KanbanColumnTitle>Active</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant={COL.ACTIVE}>{activeBlockers.length}</KanbanColumnCountBadge>
                            </KanbanColumnHeader>
                            {activeColumnBody}
                        </DroppableKanbanColumnPanel>

                        <DroppableKanbanColumnPanel columnId={BLOCKER_KANBAN_COL.RESOLVED} $fixedDesktop>
                            <KanbanColumnHeader>
                                <KanbanColumnTitle>Resolved</KanbanColumnTitle>
                                <TaskCardKanbanStatusPillCount as="span" aria-label={`${resolvedBlockers.length} resolved`}>
                                    {resolvedBlockers.length}
                                </TaskCardKanbanStatusPillCount>
                            </KanbanColumnHeader>
                            {resolvedColumnBody}
                        </DroppableKanbanColumnPanel>
                    </KanbanThreeColumnRow>
                ) : (
                    <div className="sprint-kanban-desktop-split">
                        <div data-sprint-kanban-sticky-strip className="sprint-kanban-sticky-strip">
                            <div ref={headerScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onHeaderScroll}>
                                <div style={kanbanDesktopFixedBoardHeaderRowInnerStyle}>
                                    <div style={kanbanDesktopFixedHeaderCellStyle}>
                                        <KanbanColumnHeader>
                                            <KanbanColumnTitle>Active</KanbanColumnTitle>
                                            <KanbanColumnCountBadge $variant={COL.ACTIVE}>{activeBlockers.length}</KanbanColumnCountBadge>
                                        </KanbanColumnHeader>
                                    </div>
                                    <div style={kanbanDesktopFixedHeaderCellStyle}>
                                        <KanbanColumnHeader>
                                            <KanbanColumnTitle>Resolved</KanbanColumnTitle>
                                            <TaskCardKanbanStatusPillCount as="span" aria-label={`${resolvedBlockers.length} resolved`}>
                                                {resolvedBlockers.length}
                                            </TaskCardKanbanStatusPillCount>
                                        </KanbanColumnHeader>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="sprint-kanban-desktop-body-wrap">
                            <div ref={bodyScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onBodyScroll}>
                                <div className="sprint-kanban-desktop-board-row" style={kanbanDesktopFixedBoardRowInnerStyle}>
                                    <DroppableKanbanColumnPanel columnId={BLOCKER_KANBAN_COL.ACTIVE} $fixedDesktop>
                                        {activeColumnBody}
                                    </DroppableKanbanColumnPanel>

                                    <DroppableKanbanColumnPanel columnId={BLOCKER_KANBAN_COL.RESOLVED} $fixedDesktop>
                                        {resolvedColumnBody}
                                    </DroppableKanbanColumnPanel>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </LayoutGroup>
        </SprintKanbanFill>
    );

    return (
        <Container
            className="blockers-kanban-root sprint-kanban-root"
            $display="flex"
            $flexDirection="column"
            $flex="1 1 0%"
            $minHeight="0"
            $gap="0"
            $overflow="hidden"
            style={{
                width: '100%',
                /* No milestone/sprint tab row above this board — pin column headers to the scroll top */
                '--sprint-kanban-sticky-top': '0px',
            }}
        >
            <BlockerKanbanDragCursorStyle />
            <SprintKanbanScrollChainStyle />
            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $gap="12px"
                $flexWrap="wrap"
                style={{ marginBottom: 16, flexShrink: 0 }}
            >
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 8,
                        flexWrap: 'wrap',
                    }}
                >
                    <BoardCollapseSectionHeading>Blockers</BoardCollapseSectionHeading>
                    <span
                        style={{
                            fontSize: 18,
                            fontWeight: 600,
                            fontFamily: 'var(--font-family-geist)',
                            color: token.colorTextSecondary,
                        }}
                    >
                        {total}
                    </span>
                </span>
                <Container $display="flex" $alignItems="center" $gap="8px" $flexWrap="wrap">
                    {typeof onAddBlocker === 'function' && (
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            $size="13px"
                            $padding="6px 14px"
                            icon={<PlusOutlined />}
                            onClick={onAddBlocker}
                        >
                            Add blocker
                        </Button>
                    )}
                </Container>
            </Container>

            <Container
                $display="flex"
                $flexDirection="column"
                $flex="1"
                $minHeight="0"
                $overflow="hidden"
                style={{ width: '100%' }}
            >
                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    {kanbanRow}
                    <DragOverlay>
                        {activeDragPayload?.blockerId ? (
                            <div style={{ width: 420, maxWidth: '92vw' }}>
                                <BlockerCard
                                    blocker={
                                        [...(activeBlockers ?? []), ...(resolvedBlockers ?? [])].find(
                                            (b) => String(getBlockerApiId(b) ?? b?.id ?? '') === String(activeDragPayload.blockerId),
                                        ) ?? null
                                    }
                                    column={
                                        String(activeDragPayload.sourceColumn) === String(BLOCKER_KANBAN_COL.RESOLVED)
                                            ? 'resolved'
                                            : 'active'
                                    }
                                />
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </Container>
        </Container>
    );
}
